import { ExperienceType } from '@features/DynamicPackagesConfigurationWizard/types';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { SpaEntityType } from 'shared-types/Spa';
import { getSpaTreatment, getAvailableSpaTreatmentKey } from './useGetSpaTreatment';
import { getSpaFacility, getAvailableSpaFacilityKey } from './useGetSpaFacility';

export const getSpaTreatments = async (
  propertyId: number,
  language: string,
  spaTreatmentIds: number[],
  params: { arrivalDate: string | null; isTimeSlot?: number },
  showUnpublished?: boolean,
  queryClient?: QueryClient
) => {
  const requests = spaTreatmentIds.map(async (id) => {
    const treatment = await getSpaTreatment(propertyId, language, id, {
      arrivalDate: params.arrivalDate,
    });
    if (queryClient) {
      // Update the query cache with the fetched treatment data to avoid refetching
      queryClient.setQueryData(
        getAvailableSpaTreatmentKey(
          propertyId,
          language,
          id,
          {
            arrivalDate: params.arrivalDate,
          },
          showUnpublished ?? false
        ),
        treatment
      );
    }
    return { data: treatment };
  });

  const treatmentRequests = await Promise.all(requests);
  const data = treatmentRequests.map((req) => req.data);
  return data as SpaEntityType[];
};

export const getSpaFacilities = async (
  propertyId: number,
  language: string,
  spaFacilityIds: number[],
  params: { arrivalDate: string | null },
  showUnpublished?: boolean,
  queryClient?: QueryClient
) => {
  const requests = spaFacilityIds.map(async (id) => {
    const facility = await getSpaFacility(propertyId, language, id, {
      arrivalDate: params.arrivalDate,
    });
    if (queryClient) {
      // Update the query cache with the fetched facility data to avoid refetching
      queryClient.setQueryData(
        getAvailableSpaFacilityKey(propertyId, language, id, {
          arrivalDate: params.arrivalDate,
        }),
        facility
      );
    }
    return { data: facility };
  });
  const facilitiesRequests = await Promise.all(requests);
  const data = facilitiesRequests.map((req) => req.data);
  return data as SpaEntityType[];
};

export const getSpaTreatmentsKey = (
  propertyId: number,
  language: string,
  spaTreatmentIds: number[],
  arrivalDate: string | null,
  showUnpublished = false
) => ['spaTreatments', propertyId, ...spaTreatmentIds, arrivalDate, language, showUnpublished];

export const getSpaFacilitiesKey = (
  propertyId: number,
  language: string,
  spaFacilityIds: number[],
  arrivalDate: string | null,
  showUnpublished = false
) => ['spaFacilities', propertyId, ...spaFacilityIds, arrivalDate, language, showUnpublished];

export const useGetSpaExperiences = (
  propertyId: number,
  language: string,
  options: { id: string; type: ExperienceType; name?: string }[] = [],
  arrivalDate: string | null
) => {
  const treatmentIds: number[] = [];
  const facilityIds: number[] = [];
  const classes: { id: number; type: ExperienceType }[] = [];

  options.forEach((option) => {
    if (option.type === 'spa-facilities') {
      facilityIds.push(Number(option.id));
    }
    if (option.type === 'spa-treatments') {
      treatmentIds.push(Number(option.id));
    }
    if (option.type === 'spa-classes') {
      classes.push({ ...option, id: Number(option.id) });
    }
  });

  const facilities = useQuery({
    queryKey: getSpaFacilitiesKey(propertyId, language, facilityIds, arrivalDate, true),
    queryFn: () =>
      getSpaFacilities(propertyId, language, facilityIds, { arrivalDate }, true).then((fc) =>
        fc.map((f) => ({ ...f, type: 'spa-facilities' }))
      ),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled: facilityIds.length > 0,
  });

  const treatments = useQuery({
    queryKey: getSpaTreatmentsKey(propertyId, language, treatmentIds, arrivalDate, true),
    queryFn: () =>
      getSpaTreatments(propertyId, language, treatmentIds, { arrivalDate }, true).then((tr) =>
        tr.map((t) => ({ ...t, type: 'spa-treatments' }))
      ),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled: !!treatmentIds.length,
  });

  return {
    treatments: treatments.data,
    facilities: facilities.data,
    classes,
    isFetching: treatments.isFetching || facilities.isFetching,
    error: treatments.error || facilities.error,
  };
};

export const getSpaExperiencesPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  options: { id: string; type: ExperienceType }[] = [],
  arrivalDate: string | null
) => {
  const treatmentIds: number[] = [];
  const facilityIds: number[] = [];

  options.forEach((option) => {
    if (option.type === 'spa-facilities') {
      facilityIds.push(Number(option.id));
    }
    if (option.type === 'spa-treatments') {
      treatmentIds.push(Number(option.id));
    }
  });

  await queryClient.prefetchQuery(
    getSpaFacilitiesKey(propertyId, language, facilityIds, arrivalDate, false),
    () => getSpaFacilities(propertyId, language, facilityIds, { arrivalDate }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
  await queryClient.prefetchQuery(
    getSpaTreatmentsKey(propertyId, language, treatmentIds, arrivalDate, false),
    () => getSpaTreatments(propertyId, language, treatmentIds, { arrivalDate }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
};
